/**
 * LLM evals: run a suite against model and prompt variants, then review the results.
 *
 * The suites, the runner and the judge live in obsessless-api (src/evals); this page only
 * starts runs and shows them. Runs use synthetic data, and only staging or a local API
 * serves the eval routes.
 *
 * `?load=<runId>` loads a saved run's setup into the form (the Load buttons on the run list
 * and the run page). The parameter is removed once loaded, so a reload does not undo edits.
 */
import { ListChecks, RefreshCw, Scale } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../components/Button';
import ErrorDisplay from '../../components/ErrorDisplay';
import InlineErrorDisplay from '../../components/InlineErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { NewRunForm } from './components/NewRunForm';
import { ReviewQueuePanel } from './components/ReviewQueuePanel';
import { RunList } from './components/RunList';
import { RECENT_RUNS, useEvalsData } from './hooks/useEvalsData';
import { evalsService } from './services/evalsService';
import type { RunEvalRequest } from './types/evalTypes';
import { planRerun, type RunPreset } from './utils/rerun';

export const Evals = () => {
  const { catalog, runs, reviewQueue, loading, error, reload } = useEvalsData();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  // The run goes to the background on the API; its page shows the progress.
  const start = async (request: RunEvalRequest) => {
    setStarting(true);
    setStartError(null);
    try {
      const { runId } = await evalsService.startRun(request);
      navigate(`/evals/${runId}`);
    } catch (caught) {
      setStartError(caught instanceof Error ? caught.message : 'Failed to start the run.');
      setStarting(false);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const loadRunId = searchParams.get('load');
  const [preset, setPreset] = useState<RunPreset | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!loadRunId || !catalog) return;
    let cancelled = false;
    void (async () => {
      try {
        const detail = await evalsService.getRun(loadRunId);
        const suite = catalog.suites.find((candidate) => candidate.id === detail.run.suiteId);
        const plan = planRerun(detail, suite, catalog.models);
        if (cancelled) return;
        if (plan.ok) {
          setLoadError(null);
          setPreset({ key: `${loadRunId}-${Date.now()}`, runId: loadRunId, startedAt: detail.run.startedAt, request: plan.request, notes: plan.notes });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          setLoadError(`Cannot load that run's setup: ${plan.reason}`);
        }
      } catch (caught) {
        if (!cancelled) setLoadError(caught instanceof Error ? caught.message : "Failed to load that run's setup.");
      } finally {
        if (!cancelled) setSearchParams({}, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRunId, catalog, setSearchParams]);

  // Only the first load: on a refresh the form stays mounted and keeps what was entered.
  if (loading && !catalog) return <LoadingSpinner message="Loading evals…" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
            <h1 className="text-3xl font-bold text-gray-900">Evals</h1>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" icon={<ListChecks size={13} />} onClick={() => navigate('/evals/cases')}>
                Cases
              </Button>
              <Button variant="outline" size="sm" icon={<Scale size={13} />} onClick={() => navigate('/evals/judge-prompts')}>
                Judge prompts
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Run an eval suite against model and prompt variants, then review the results before a change goes live. Runs go
            on in the background, so you can close the tab. Synthetic data only.
          </p>
        </div>

        {error && <ErrorDisplay message={error} />}
        {loadError && <InlineErrorDisplay message={loadError} onDismiss={() => setLoadError(null)} />}
        {reviewQueue && <ReviewQueuePanel queue={reviewQueue} />}

        {catalog && (
          <NewRunForm
            key={preset?.key ?? 'blank'}
            catalog={catalog}
            running={starting}
            onStart={(request) => void start(request)}
            preset={preset}
            onClearPreset={() => setPreset(null)}
          />
        )}
        {startError && <InlineErrorDisplay message={startError} onDismiss={() => setStartError(null)} />}

        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <h2 className="text-lg font-semibold text-gray-900">Recent runs</h2>
            <div className="flex items-center gap-3">
              {runs.length >= RECENT_RUNS && (
                <Link to="/evals/runs" className="text-sm text-blue-600 hover:underline">
                  All runs →
                </Link>
              )}
              <Button variant="outline" size="sm" icon={<RefreshCw size={13} />} loading={loading} disabled={loading} onClick={() => void reload()}>
                Refresh
              </Button>
            </div>
          </div>
          <RunList runs={runs} models={catalog?.models ?? []} suites={catalog?.suites ?? []} />
        </div>
      </div>
    </div>
  );
};
